import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SectionHeading from '../common/SectionHeading';
import PricingCard from '../common/PricingCard';

const SectionContainer = styled.section`
  background-color: #161616;
  color: #FFFFFF;
  padding: 100px 0;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const SpecialOfferBanner = styled(motion.div)`
  background-color: rgba(255, 215, 0, 0.15);
  border: 1px solid #FFD700;
  border-radius: 8px;
  padding: 16px 24px;
  margin: 24px auto 48px;
  text-align: center;
  max-width: 800px;
  
  p {
    color: #FFD700;
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: 18px;
    margin: 0;
  }
  
  @media (max-width: 768px) {
    p {
      font-size: 16px;
    }
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin: 60px 0;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const SEOContent = styled.div`
  margin-top: 60px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const SEOHeading = styled.h2`
  font-size: 28px;
  color: #2196F3;
  margin-bottom: 24px;
  font-weight: 700;
`;

const SEOText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 16px;
  
  strong {
    color: #FFD700;
    font-weight: 600;
  }
`;

const PricingSection = () => {
  const plans = [
    {
      title: "Free",
      regularPrice: null,
      salePrice: "Free",
      period: "",
      features: [
        "1 Credit to get report",
        "Access to CarConsul for 1 month",
        "Deep Vehicle Research",
        "Market Price Analysis",
        "Basic Negotiation Tips"
      ],
      isPopular: false,
      buttonVariant: "primary",
      buttonText: "Start Free",
      trialDays: 0
    },
    {
      title: "Unique Deal",
      regularPrice: null,
      salePrice: "$10",
      period: "",
      features: [
        "10 Credits to run reports",
        "Access to CarConsul for 6 months",
        "Advanced Negotiation Scripts",
        "Contract Review Assistance",
        "Dealer Reputation Analysis",
        "Personalized Buying Strategy"
      ],
      isPopular: true,
      buttonVariant: "gold",
      buttonText: "Get Deal"
    },
    {
      title: "Dealership & Fleet",
      regularPrice: null,
      salePrice: "Contact Us",
      period: "",
      features: [
        "Unlimited Research",
        "API Access",
        "Custom Branding",
        "Analytics Dashboard",
        "Priority Support"
      ],
      isPopular: false,
      buttonVariant: "secondary",
      buttonText: "Contact Sales",
      trialDays: 0
    }
  ];

  return (
    <SectionContainer id="pricing">
      <Content>
        <SectionHeading 
          title="Car Price Negotiation & Buying Advisor Plans" 
          subtitle="Professional car buying assistance with proven negotiation strategies to save thousands on your next vehicle purchase"
        />
        
        <SpecialOfferBanner
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p>🔥 30 days money back guarantee. No questions. No subscription. 🔥</p>
        </SpecialOfferBanner>
        
        <PricingGrid>
          {plans.map((plan, index) => (
            <PricingCard
              key={index}
              title={plan.title}
              regularPrice={plan.regularPrice}
              salePrice={plan.salePrice}
              period={plan.period || ""}
              features={plan.features}
              isPopular={plan.isPopular}
              trialDays={plan.trialDays !== undefined ? plan.trialDays : 7}
              buttonText={plan.buttonText || "Try Now"}
              buttonVariant={plan.buttonVariant}
            />
          ))}
        </PricingGrid>
        
        <SEOContent>
          <SEOHeading>Why Choose Our Car Price Negotiation App?</SEOHeading>
          <SEOText>
            Our <strong>AI-powered car buying advisor</strong> combines advanced market analysis with proven negotiation techniques to help you secure the best possible deal on your next vehicle. Unlike traditional car buying services, we provide completely independent advice with no dealer affiliations.
          </SEOText>
          <SEOText>
            With our <strong>car price negotiation app</strong>, you'll receive personalized negotiation scripts, real-time market pricing data, and step-by-step guidance through every aspect of the car buying process. Our users typically save <strong>$2,000-$5,000</strong> compared to buyers who negotiate without professional assistance.
          </SEOText>
          <SEOText>
            Whether you're buying from a dealership or private seller, our <strong>car buying consultant AI</strong> analyzes vehicle history, market trends, financing options, and dealer reputation to ensure you make an informed decision. Get professional-grade car buying advice at a fraction of the cost of hiring a traditional consultant.
          </SEOText>
        </SEOContent>
      </Content>
    </SectionContainer>
  );
};

export default PricingSection;
